import { useEffect, useMemo, useState } from "react";
import { Alert, Button, Card } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { QuestionnaireDisplay, ValueSetLoader } from "@fyrstain/hl7-front-library";
import Client from "fhir-kit-client";
import i18n from "i18next";
import type { Questionnaire, QuestionnaireResponse } from "fhir/r5";

import QuestionnaireService from "../../services/QuestionnaireService";
import type { DeviceKind } from "../../services/DeviceAdapter";

type Props = { kind: DeviceKind; onBack: () => void };

const DeviceQuestionnaireRegistration = ({ kind, onBack }: Props) => {
  const navigate = useNavigate();
  const questionnaireId = kind === "software" ? "Questionnaire-Software" : "Questionnaire-Material";
  const detailsRoute = kind === "software" ? "/SoftwareViewer/" : "/MaterialViewer/";
  const valueSetLoader = useMemo(
    () => new ValueSetLoader(new Client({ baseUrl: process.env.REACT_APP_FHIR_URL ?? "fhir" })),
    [],
  );
  const [questionnaire, setQuestionnaire] = useState<Questionnaire>();
  const [response, setResponse] = useState<QuestionnaireResponse>();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const [saved, setSaved] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setLoadError(false);
    setQuestionnaire(undefined);
    setResponse(undefined);

    async function load() {
      try {
        const loadedQuestionnaire = await QuestionnaireService.loadQuestionnaire(questionnaireId);
        if (!loadedQuestionnaire.item?.length) {
          throw new Error(`The ${kind} questionnaire has no items.`);
        }
        const populated = await QuestionnaireService.populate(
          loadedQuestionnaire,
          process.env.REACT_APP_QUESTIONNAIRE_SUBJECT_ID ?? "123",
        );
        if (active) {
          setQuestionnaire(loadedQuestionnaire);
          setResponse(populated);
        }
      } catch (error) {
        console.error(`Unable to load ${kind} questionnaire`, error);
        if (active) setLoadError(true);
      } finally {
        if (active) setLoading(false);
      }
    }

    load();
    return () => { active = false; };
  }, [attempt, kind, questionnaireId]);

  const handleSubmit = async (submitted: QuestionnaireResponse) => {
    if (saving) return;
    setSaving(true);
    setSubmitError(false);
    try {
      const devices = await QuestionnaireService.submitDevice(submitted, kind);
      if (devices[0]?.id) {
        navigate(`${detailsRoute}${devices[0].id}`);
      } else {
        setSaved(true);
      }
    } catch (error) {
      console.error(`Unable to submit ${kind} questionnaire`, error);
      setSubmitError(true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="device-registration-card">
      <Card.Header>{i18n.t(kind === "software" ? "devices.registration.questionnaireSoftwareTitle" : "devices.registration.questionnaireMaterialTitle")}</Card.Header>
      <Card.Body>
        {loading && <p role="status">{i18n.t("devices.registration.questionnaireLoading")}</p>}
        {loadError && (
          <Alert variant="danger">
            {i18n.t("devices.registration.questionnaireLoadFailed")}
            <Button className="ms-2" size="sm" variant="outline-danger" onClick={() => setAttempt((value) => value + 1)}>
              {i18n.t("button.retry")}
            </Button>
          </Alert>
        )}
        {questionnaire && response && (
          <QuestionnaireDisplay
            language={i18n.t}
            questionnaire={questionnaire}
            questionnaireResponse={response}
            valueSetLoader={valueSetLoader}
            onSubmit={handleSubmit}
            readOnly={saving || saved}
          />
        )}
        {saving && <p role="status">{i18n.t("devices.registration.questionnaireSaving")}</p>}
        {submitError && <Alert variant="danger">{i18n.t("devices.registration.questionnaireSubmitFailed")}</Alert>}
        {saved && <Alert variant="success">{i18n.t("devices.registration.success")}</Alert>}
        <Button className="mt-3" type="button" variant="outline-secondary" onClick={onBack}>
          {i18n.t("button.back")}
        </Button>
      </Card.Body>
    </Card>
  );
};

export default DeviceQuestionnaireRegistration;
